import "../../styles/global.css";
import "./Overview.css";
import React, { useState, useEffect } from "react";
import { fetchLtiSessionInfo, 
    fetchSubmissionCount, fetchDemoSubmissionCount, 
    fetchQuestionAverage, fetchDemoQuestionAverage, 
    fetchAllSubmissionDates, fetchAllDemoSubmissionDates, 
    fetchClickTimeRange, fetchDemoClickTimeRange,
    fetchStudentCount, fetchCourseName } from "../../api/overviewApi";
import GroupedBarChart from "./GroupedBarChart";
import QuestionTable from "./QuestionTable";
import { Slider, Typography, Chip } from "@mui/material";
import dayjs from "dayjs";
import { FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import utc from 'dayjs/plugin/utc';


dayjs.extend(utc);
  
const Overview = () => {
    const [submissionCount, setSubmissionCount] = useState(null);
    const [questionAverage, setQuestionAverage] = useState([]);
    const [sessionInfo, setSessionInfo] = useState(null);
    const [error, setError] = useState(null);
    const [allDates, setAllDates] = useState([]);
    const [dateRange, setDateRange] = useState([null, null]);
    const [submissionRange, setSubmissionRange] = useState([null, null]);
    const [clickTimeRange, setClickTimeRange] = useState(null);
    const [studentCount, setStudentCount] = useState(null);
    const [courseName, setCourseName] = useState("Demo Course");

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const token = params.get('token');

        console.log("🔑 Token from URL:", token);

        // const user = {};
        // user.courseId = 190480;
        // setSessionInfo(user);
        // localStorage.setItem('iTOOLS_sessionInfo', JSON.stringify(user));

        if (token) {
          fetch('https://api.olaitools.com/api/dashboard/lti-session-info', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ token })
          })
          .then(res => res.json())
          .then(data => {
            console.log("Decoded user info:", data);
            const user = data.user;
            if (user.courseId === '93298') {
                console.log("Overriding 93298 to be treated as a demo course");
                user.courseId = undefined;  // makes it follow demo logic
                user.name = "Demo Course";  // optional: rename for clarity
            } else {
                console.log("Not 93298 - User courseId:", user.courseId);
            }
            setSessionInfo(user);
            localStorage.setItem('iTOOLS_sessionInfo', JSON.stringify(user));                                                      
          })
          .catch(err => {
            console.error("Failed to fetch user info:", err);
          });
        }

        const cached = localStorage.getItem('iTOOLS_sessionInfo');
        if (cached) {
            console.log("Using cached session info");
            setSessionInfo(JSON.parse(cached));
        } else {
            console.log("No cached session info found");
            setSessionInfo({ name: "Demo Course" }); // Fallback to demo
        }
    }, []);
    // useEffect(() => { // for localhost testing only
    //     const params = new URLSearchParams(window.location.search);
    //     const token = params.get('token');
    //     console.log("🔑 Token from URL:", token);

    //     const cached = localStorage.getItem('iTOOLS_sessionInfo');

    //     if (cached) {
    //         console.log("✅ Using cached session info");
    //         setSessionInfo(JSON.parse(cached));
    //     } else {
    //         console.log("⚠️ No cached session info found, using hardcoded default for localhost");

    //         const user = { courseId: 176764 };
    //         setSessionInfo(user);
    //         localStorage.setItem('iTOOLS_sessionInfo', JSON.stringify(user));
    //     }

    // }, []);

    // useEffect(() => {
    //     const getSessionInfo = async () => {
    //         try {
    //             const data = await fetchLtiSessionInfo();
    //             console.log('LTI session info:', data);
    //             setSessionInfo(data);
    //         } catch (error) {
    //             console.error('Error fetching LTI session info:', error);
    //             setSessionInfo({ name: "Demo Course" });
    //         }   
    //     }
    //     getSessionInfo();
    // }, []);

    // fetch min and max dates for slider
    useEffect(() => {
        if (!sessionInfo) return;
  
        console.log("Fetching real course submission dates...");
        fetchAllSubmissionDates(sessionInfo?.courseId)
            .then((data) => {
                const raw = Array.isArray(data?.dates) ? data.dates : [];
                const ts = raw
                    .map((d) => Date.parse(d))
                    .filter((n) => Number.isFinite(n));
                if (ts.length === 0) {
                    // cold start: no submissions yet
                    setAllDates([]);
                    setDateRange([null, null]);
                    setSubmissionRange([null, null]);
                    return;
                }
                const minDate = Math.min(...ts);
                const maxDate = Math.max(...ts);
                setDateRange([minDate, maxDate]);
                setSubmissionRange([minDate, maxDate]);
                console.log("Min and max submission dates:", minDate, maxDate);
             
                const start_fa25 = Date.parse("2025-08-18T00:00:00-07:00");
                const end_now = Date.now(); // "now"
                setAllDates([start_fa25, end_now]);
            })
            .catch((error) => {
                console.error("Error fetching all submission dates:", error);
                setAllDates([]);
                setDateRange([null, null]);
            });
    }, [sessionInfo]);

    useEffect(() => {
        if (!sessionInfo) return;
        console.log("Fetching student count...");
        fetchStudentCount(sessionInfo.courseId)
            .then((data) => {
                console.log("Student count data:", data);
                setStudentCount(data.count);
            })
            .catch((err) => {
                console.error("Error fetching student count:", err);
            });
    }, [sessionInfo]);

    useEffect(() => {
        if (!sessionInfo) return;

        console.log("Fetching click time range...");
        fetchClickTimeRange(sessionInfo.courseId)
            .then((data) => {
                console.log("Click time range data:", data);
                if (data && data.click_timerange) {
                    setClickTimeRange(data.click_timerange);
                } else {
                    console.warn("No time range returned");
                }
            })
            .catch((err) => {
                console.error("Error fetching click time range:", err);
            });
    }, [sessionInfo]);

    useEffect(() => {
        if (!sessionInfo) return;

        console.log("Fetching course name...");
        fetchCourseName(sessionInfo.courseId)
            .then((name) => {
                console.log("? Course name:", name);
                setCourseName(name);
            })
            .catch((err) => {
                console.error("Error fetching course name:", err);
            });
    }, [sessionInfo]);

    useEffect(() => {
        const isFiniteTs = (x) => Number.isFinite(x) && x > 0;
        if (!sessionInfo || !isFiniteTs(dateRange[0]) || !isFiniteTs(dateRange[1])) return;
        console.log("overview.js - allDates, dateRange:", allDates, dateRange);
        if (dateRange[0] && dateRange[1]) {
          const [startISO, endISO] = dateRange.map((ts) =>
            new Date(ts).toISOString()
          );

          console.log("overview.js - startISO, endISO:", startISO, endISO, sessionInfo.courseId);

          fetchSubmissionCount(startISO, endISO, sessionInfo.courseId)
            .then(setSubmissionCount)
            .catch(() => setError("Failed to load submission count"));
    
          fetchQuestionAverage(startISO, endISO, sessionInfo.courseId)
            .then((data) => setQuestionAverage(data?.questions ?? []))
            .catch(() => setError("Failed to load question averages"));

        }
    }, [dateRange, sessionInfo]);

    const handleSliderChange = (event, newValue) => {
        setDateRange(newValue);
    };

    return (
        <div className="wrapper">
        <p className="step-overview-1">
            Welcome to iTOOLS! This dashboard provides an overview of student
            engagement and learning activities.
        </p>

        <div className="dashboard-box">

        
            <div className="section-content">
                {/* Left panel: controls */}
                <div className="controls-panel">
                    <Typography gutterBottom>Current class:</Typography>
                    <Chip
                        label={courseName}
                        variant="outlined"
                        sx={{
                            mb: 2,
                            height: 40,            // default ~32
                            fontSize: '1rem',      // bump text
                            '& .MuiChip-label': {
                            px: 1.5,             // left/right padding
                            py: 0.25,            // optional top/bottom padding
                            fontWeight: 600
                            }
                        }}
                    />

                    {Number.isFinite(allDates[0]) && Number.isFinite(allDates[1]) && 
                        Number.isFinite(dateRange[0]) && Number.isFinite(dateRange[1]) && (
                        <>
                            <Typography gutterBottom>Select time range of survey submissions:</Typography>
                            <div className="slider-wrapper">
                            <Slider
                                value={dateRange}
                                onChange={handleSliderChange}
                                valueLabelDisplay="auto"
                                min={allDates[0]}
                                max={allDates[1]}
                                step={24 * 60 * 60 * 1000}
                                valueLabelFormat={(ts) => dayjs(ts).format("MMM D")}
                            />
                            </div>
                        </>
                    )}
                </div>

                {/* Right panel: submission count and question averages */}
                <div className="stats-panel">
                    {!Number.isFinite(dateRange[0]) || !Number.isFinite(dateRange[1]) ? (
                        <p>No survey submissions yet for this course.</p>
                    ) : error ? (
                        <p style={{ color: "red" }}>{error}</p>
                    ) : submissionCount !== null ? (
                        <div className="stats-grid">
                            <div className="stats-column">
                                <p><strong>Total Responses</strong><br />{submissionCount.count} submission(s)</p>
                                <p><strong>Survey From</strong><br />{submissionRange[0] ? dayjs.utc(submissionRange[0]).format("MM/DD/YYYY h:mm A") + " UTC" : "N/A"}</p>
                                <p><strong>Click Data From</strong><br />{clickTimeRange?.start_time ? dayjs.utc(clickTimeRange.start_time).format("MM/DD/YYYY h:mm A") + " UTC" : "N/A"}</p>
                            </div>

                            <div className="stats-column">
                                <p>
                                    <strong>Total Respondents</strong><br />
                                    {sessionInfo?.courseId ? `${studentCount} student(s)` : '11 student(s)'}
                                </p>
                                <p><strong>To</strong><br />{submissionRange[1] ? dayjs.utc(submissionRange[1]).format("MM/DD/YYYY h:mm A") + " UTC" : "N/A"}</p>
                                <p><strong>To</strong><br />{clickTimeRange?.end_time ? dayjs.utc(clickTimeRange.end_time).format("MM/DD/YYYY h:mm A") + " UTC" : "N/A"}</p>
                            </div>
                        </div>
                
                    ) : (
                        <p>Loading...</p>
                    )}
                </div>
            </div>

        </div>

        

        {questionAverage.length > 0 && (
            <>
                <h2>Measure-Level Averages</h2>
                <GroupedBarChart questionAverage={questionAverage} />
            </>
        )}
        
        <QuestionTable />
      
        </div>
    );
};

export default Overview;
